import {client} from '@passwordless-id/webauthn';
import BaseAuth from './BaseAuth';
import base64url from 'base64url';
import {Decoder as cbor} from "cbor";
import PasskeyTableManager from "./passkey-table-manager";

const BUTTON_CLASSES = {
    pending: ['success', 'failed', 'login', 'login-failed'],
    success: ['pending', 'failed', 'login', 'login-failed'],
    failed: ['pending', 'success', 'login', 'login-failed'],
    login: ['pending', 'success', 'failed', 'login-failed'],
    'login-failed': ['pending', 'success', 'failed', 'login'],
};

const knownAuthenticators = {
    'ea9b8d664d011d213ce4b6b48cb575d4': 'Google Password Manager',
    'adce000235bcc60a648b0b25f1f05503': 'Chrome on Mac',
    '08987058cadc4b81b6e130de50dcbe96': 'Windows Hello',
    '9ddd1817af5a4672a2b93e3dd95000a9': 'Windows Hello',
    '6028b017b1d44c02b4b3afcdafc96bb2': 'Windows Hello',
    'dd4ec289e01d41c9bb8970fa845d4bf2': 'iCloud Keychain (Managed)',
    '531126d6e717415c93203d9aa6981239': 'Dashlane',
    'bada5566a7aa401fbd9645619a55120d': '1Password',
    'b84e404815dc4dd08640f4f60813c8af': 'NordPass',
    '0ea242b443c44a1b8b17dd6d0b6baec6': 'Keeper',
    '891494da2c904d31a9cd4eab0aed1309': 'Sésame',
    'f38095407f1449c1a8b38f813b225541': 'Enpass',
    'b53976664885aa6bcebf-e52262a439a2': 'Chromium Browser',
    '771b48fdd3d44f749232fc157ab0507a': 'Edge on Mac',
    '39a5647e1853446ca1f6a79bae9f5bc7': 'IDmelon',
    'd548826e79b4db40a3d811116f7e8349': 'Bitwarden',
    'fbfc3007154e4ecc8c0b6e020557d7bd': 'iCloud Keychain',
    '53414d53554e47000000000000000000': 'Samsung Pass',
    '66a0ccb3bd6a191fee06e375c50b9846': 'Thales Bio iOS SDK',
    '8836336af5900921301d46427531eee6': 'Thales Bio Android SDK',
    'cd69adb53c7adeb931776800ea6cb72a': 'Thales PIN Android SDK',
    '17290f1ec21234d01423365d729f09d9': 'Thales PIN iOS SDK',
    '50726f746f6e5061737350726f746f6e': 'Proton Pass',
    'fdb141b25d84443e8a354698c205a502': 'KeePassXC',
    'cc45f64e52a2451b831a4edd8022a202': 'ToothPic Passkey Provider'
};

class Passkey extends BaseAuth {
    constructor(root, settings) {
        super(root, settings);
        this.passkey = '';
        this.onboarding = false;
        this.manager = new PasskeyTableManager(this.root, this.settings);
        this.translate = {
            webauthn_not_available: this.settings.translatables.webauthn_not_available,
            unknown_error: this.settings.translatables.unknown_error,
            response_error: this.settings.translatables.response_error,
            passkey_not_found: this.settings.translatables.passkey_not_found,
            passkey_login_error: this.settings.translatables.passkey_login_error,
            passkey_registration_error: this.settings.translatables.passkey_registration_error,
            passkey_login_success: this.settings.translatables.passkey_login_success,
            passkey_configuration: this.settings.translatables.passkey_configuration,
            error_assertion: this.settings.translatables.error_assertion,
            notice_inform: this.settings.translatables.notice_inform,
            register_passkey: this.settings.translatables.register_passkey,
            network_not_ok: this.settings.translatables.network_not_ok,
            error_complete_registration: this.settings.translatables.error_complete_registration,
            usb: this.settings.translatables.usb,
            nfc: this.settings.translatables.nfc,
            ble: this.settings.translatables.ble,
            internal: this.settings.translatables.internal,
            cross_platform: this.settings.translatables.cross_platform,
            unknown: this.settings.translatables.unknown,
            pending: this.settings.translatables.pending,
            success: this.settings.translatables.success,
            login: this.settings.translatables.login,
            login_failed: this.settings.translatables.login_failed,
            passkey_configure_manual_login: this.settings.translatables.passkey_configure_manual_login,
        }
        this.BUTTON_TEXT = {
            pending: this.settings.translatables.pending,
            success: this.settings.translatables.success,
            failed: this.settings.translatables.failed,
            login: this.settings.translatables.login,
            'login-failed': this.settings.translatables.login_failed,
        };
    }

    init() {
        const stepThree = this.getElement('rsssl_step_three_onboarding');

        if (stepThree !== null) {
            const observer = new MutationObserver(this.handleMutation.bind(this));
            observer.observe(stepThree, {attributes: true});
        }

        if (document.getElementById('rsssl-add-passkey-button')) {
            this.assignClickListener('rsssl-add-passkey-button', this.handlePasskeyRegister.bind(this));
        }
    }

    handleMutation(mutations) {
        mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                const displayStyle = window.getComputedStyle(mutation.target).display;
                if (displayStyle === 'block') {
                    this.showPasskeyOptions();
                }
            }
        });
    }

    logButtonState = (id) => {
        return document.getElementById(id);
    }
    updateRegisterButtonState(state) {
        setTimeout(() => {
            let workButton = this.logButtonState('register-passkey-button')
                || this.logButtonState('rsssl-passkey-button')
                || this.logButtonState('rsssl-add-passkey-button');
            if (!workButton) return;

            workButton.innerHTML = this.BUTTON_TEXT[state] || 'Unknown State';
            workButton.disabled = state === 'pending';
            workButton.classList.remove(...(BUTTON_CLASSES[state] || []));
            workButton.classList.add(state);
            // If login failed on the passkey button, make click retry the login challenge
            if (state === 'login-failed' && workButton.id === 'rsssl-passkey-button') {
                workButton.type = 'button';
                workButton.addEventListener('click', e => {
                    e.preventDefault();
                    this.handlePasskeyLogin();
                });
            }
            if (!this.BUTTON_TEXT[state]) {
                console.error('Unknown state:', state);
            }
        }, 100); // Adjust the timeout duration as necessary
    }

    async handlePasskeyLogin() {
        this.updateRegisterButtonState('login');

        if (!client.isAvailable()) {
            this.handleError(this.translate.webauthn_not_available, 'login-failed');
            return;
        }

        try {
            const userHandle = localStorage.getItem('webauthn_user_handle');
            const userNameElement = document.getElementById('user_login');
            const user_login = userNameElement ? userNameElement.value : '';
            const requestData = {credentials: 'include', userHandle, user_login};

            const response = await this.performFetchOp('/webauthn_challenge_assertion', requestData);

            if (!response.ok) {
                if (response.status === 400) {
                    try {
                        const errorData = await response.json();
                        if (errorData && errorData.error) {
                            this.handleError(errorData.error, 'login-failed');
                        } else {
                            this.handleError(this.translate.passkey_configure_manual_login, 'login-failed');
                        }
                    } catch (jsonError) {
                        this.handleError(this.translate.passkey_configure_manual_login, 'login-failed');
                    }
                    return;
                }
                // Handle other non-OK responses
                try {
                    const errorData = await response.json(); 
                    if (errorData && errorData.error) {
                        this.handleError(errorData.error, 'login-failed');
                    } else {
                        this.handleError(this.translate.unknown_error, 'login-failed'); 
                    }
                } catch (error) {
                    console.error('Failed to parse JSON from other error response or unknown error:', error);
                    this.handleError(this.translate.response_error, 'login-failed');
                }
                return; // Ensure we exit after handling non-OK responses
            }
            const publicKeyCredentialRequestOptions = await this.prepareCredentialRequest(response, userHandle);
            const assertion = await navigator.credentials.get({publicKey: publicKeyCredentialRequestOptions});
            await this.sendAssertion(assertion);
        } catch (error) {
            if (error.name === 'NotAllowedError') {
                this.handleError(this.translate.passkey_not_found, 'login-failed');
            } else {
                this.handleError(this.translate.passkey_login_error, 'login-failed', error);
            }
        }
    }

    async prepareCredentialRequest(response, userHandle) {
        const options = await response.json();
        options.challenge = base64url.toBuffer(options.challenge);

        if (options.allowCredentials) {
            options.allowCredentials = options.allowCredentials.map(cred => ({
                ...cred,
                id: base64url.toBuffer(cred.id),
            }));
        }

        if (userHandle) {
            options.userHandle = base64url.toBuffer(userHandle);
        }

        return options;
    }

    async sendAssertion(assertion) {
        const data = {
            credential: {
                id: assertion.id,
                rawId: base64url.encode(assertion.rawId),
                response: {
                    authenticatorData: base64url.encode(assertion.response.authenticatorData),
                    clientDataJSON: base64url.encode(assertion.response.clientDataJSON),
                    signature: base64url.encode(assertion.response.signature),
                    userHandle: base64url.encode(assertion.response.userHandle),
                },
                type: assertion.type,
            },
            onboarding: this.onboarding,
            redirect_to: this.settings.redirect_to,
        };

        try {
            console.log('Attempting /webauthn_verify_assertion with data:', data);
            const response = await this.performFetchOp('/webauthn_verify_assertion', data);
            
            if (!response.ok) {
                console.log('/webauthn_verify_assertion failed with status:', response.status);
                if (response.status === 400) {
                    console.log('Handling 400 error from /webauthn_verify_assertion');
                    try {
                        const errorData = await response.json();
                        if (errorData && errorData.error) {
                            console.log('400 error data from verify_assertion:', errorData);
                            this.handleError(errorData.error, 'login-failed');
                        } else {
                            console.log('400 error from verify_assertion, but no specific error message in JSON. Falling back to manual configure message.');
                            this.handleError(this.translate.passkey_configure_manual_login, 'login-failed');
                        }
                    } catch (jsonError) {
                        console.error('Failed to parse JSON from 400 error response (verify_assertion):', jsonError);
                        this.handleError(this.translate.passkey_configure_manual_login, 'login-failed');
                    }
                } else {
                    // Handle other non-400 errors for verify_assertion
                    const result = await response.json(); // Attempt to parse for other errors too
                    console.log('Error during /webauthn_verify_assertion, result:', result);
                    this.handleError(result.message || this.translate.error_assertion, 'login-failed');
                }
                return; // Exit after handling error
            }

            // If response.ok is true
            const result = await response.json();
            console.log('/webauthn_verify_assertion response result:', result);

            if (result.status === 'success') {
                this.updateRegisterButtonState('success');
                if (result.redirect_to.includes('rsssl_no_redirect')) {
                    this.manager.fetchDataAndRender();
                } else {
                    const form = document.querySelector('form');
                    if (form) {
                        form.reset();
                    }
                    window.onbeforeunload = null;
                    window.location.href = result.redirect_to;
                }
            } else {
                this.updateRegisterButtonState('login-failed');
            }
        } catch (error) {
            console.error('Error in sendAssertion catch block:', error);
            this.handleError(this.translate.error_assertion, 'login-failed', error);
        }
    }

    showPasskeyOptions() {
        // first we change the h3 text and the p text with the instructions
        const mainElement = this.getElement('two_fa_onboarding_form');
        if (mainElement) {
            mainElement.querySelector('h3').innerText = this.settings.translatables.passkey_configuration;
            document.querySelector('.rsssl_into_two_factor').innerHTML = this.settings.translatables.notice_inform;
        }
        // this.getElement('rsssl_step_three_onboarding').getElement('rsssl_into_two_factor').innerText = 'rsssl_into_two_factor';
        let container = this.getElement('passkey-integration');
        if (!container) {
            container = document.createElement('div');
            container.id = 'passkey-integration';
            document.body.appendChild(container);
        }

        container.innerHTML = `<button id="register-passkey-button" class="passkey-registration-button">${this.settings.translatables.register_passkey}</button>`;
        this.assignClickListener('register-passkey-button', this.handlePasskeyRegister.bind(this));
    }

    async handlePasskeyRegister() {
        // We prevent the default behavior of the button to avoid form submission
        this.updateRegisterButtonState('pending');
        if (!client.isAvailable()) {
            this.handleError(this.settings.translatables.webauthn_not_available, 'failed');
            return;
        }

        try {
            const publicKeyCreationOptions = await this.fetchPublicKeyOptions();
            const credential = await navigator.credentials.create({publicKey: publicKeyCreationOptions});
            const registrationResult = await this.completeRegistration(credential);
            if (registrationResult.status !== 'success') {
                this.updateRegisterButtonState('failed');
            }
            if (registrationResult.redirect_to.includes('rsssl_no_redirect')) {
                // we reload the page to show the new profile
                const form = document.querySelector('form');
                if (form) {
                    form.reset();
                }
                window.onbeforeunload = null;
                window.location.reload();
            } else {
                const form = document.querySelector('form');
                if (form) {
                    form.reset();
                }
                window.onbeforeunload = null;
                window.location.href = registrationResult.redirect_to;
            }
        } catch (error) {
            this.handleError(this.translate.error_complete_registration, 'failed', error);
        }
    }

    async fetchPublicKeyOptions() {
        const response = await this.performFetchOp('/webauthn_register_callback', {
            user_id: this.settings.user_id,
            login_nonce: this.settings.login_nonce,
            provider: 'passkey_register',
        });

        if (!response.ok) throw new Error(this.translate.network_not_ok);

        const options = await response.json();

        base64url.toBuffer(this.generateUniqueUserHandle());
        options.challenge = base64url.toBuffer(options.challenge); // challenge as Uint8Array
        options.user.id = base64url.toBuffer(options.user.id); // user.id as Uint8Array
        options.user.name = this.settings.display_name || options.user.name;
        options.user.displayName = this.settings.display_name || options.user.displayName;
        options.pubKeyCredParams = [{type: 'public-key', alg: -7}, {type: 'public-key', alg: -257}];
        options.rp.id = window.location.hostname;
        options.authenticatorSelection = {authenticatorAttachment: 'platform', userVerification: 'preferred'};

        return options;
    }

    async completeRegistration(credential) {
        const deviceName = this.getDeviceName(credential);

        const data = {
            credential: {
                id: credential.id,
                rawId: base64url.encode(credential.rawId),
                response: {
                    attestationObject: base64url.encode(credential.response.attestationObject),
                    clientDataJSON: base64url.encode(credential.response.clientDataJSON),
                },
                type: credential.type,
                authDevice: deviceName,
            },
            user_id: this.settings.user_id,
            redirect_to: this.settings.redirect_to,
            device_name: deviceName,
            login_nonce: this.settings.login_nonce,
        };

        try {
            const response = await this.performFetchOp('/webauthn_complete_registration', data);
            const result = await response.json();

            if (response.ok && result.status === 'success') {
                if (result.userHandle) {
                    localStorage.setItem('webauthn_user_handle', result.userHandle);
                }
                this.updateRegisterButtonState('success');
                this.onboarding = true;
                await this.handlePasskeyLogin();
                return {status: 'success', redirect_to: result.redirect_to};
            } else {
                return {status: 'failed', reason: 'registration_failed'};
            }
        } catch (error) {
            this.handleError(this.translate.error_complete_registration, 'failed', error);
            return {status: 'failed', reason: 'error_occurred'};
        }
    }

    async fireRegistration(automatic = true) {
        if (automatic) {
            const result = await this.handlePasskeyRegister();
            this.updateRegisterButtonState(result.status === 'success' ? 'success' : 'failed');
        } else {
            this.assignClickListener('register-passkey-button', this.handlePasskeyRegister.bind(this));
        }
    }

    handleError(message, state, error = null) {
        this.updateRegisterButtonState(state);
        // show the error message in the error container
        const errorContainer = this.getElement('rsssl-passkey-error');
        if (errorContainer) {
            errorContainer.innerText = message;
            errorContainer.style.display = 'block';
        }
        console.error(message, error);
    }

    getDeviceName(credential) {
        let deviceName = 'unknown';
        let authenticatorAttachment = credential.authenticatorAttachment || 'unknown';

        const attestationObject = credential.response.attestationObject;

        // Decode the attestation object
        const decodedAttestationObject = cbor.decodeAllSync(new Uint8Array(attestationObject))[0];
        const authData = new Uint8Array(decodedAttestationObject.authData);

        // Extract the aaguid (bytes 37-52 of authData)
        const aaguid = authData.slice(37, 53);
        const aaguidString = Array.from(aaguid).map(byte => byte.toString(16).padStart(2, '0')).join('');
        if (knownAuthenticators[aaguidString]) {
            return knownAuthenticators[aaguidString];
        }

        if (typeof credential.response.getTransports === 'function') {
            const transports = credential.response.getTransports();
            if (transports && transports.length > 0) {
                authenticatorAttachment = transports[0]; // Use the first available transport method
            }
        }

        switch (authenticatorAttachment) {
            case 'usb':
                deviceName = this.settings.translatables.usb;
                break;
            case 'nfc':
                deviceName = this.settings.translatables.nfc;
                break;
            case 'ble':
                deviceName = this.settings.translatables.ble;
                break;
            case 'internal':
                deviceName = this.settings.translatables.internal;
                break;
            case 'cross-platform':
                deviceName = this.settings.translatables.cross_platform;
                break;
            default:
                deviceName = this.settings.translatables.unknown;
        }
        return deviceName;
    }

    generateUniqueUserHandle() {
        return `${this.settings.user_id}-${Date.now()}`;
    }
}
export default Passkey;